class ArchiveFilter extends HTMLElement {
  constructor() {
    super()

    this.filterForm = this.querySelector('form')
    this.filterInputs = this.filterForm.querySelectorAll('input[type="checkbox"]')
    this.articles = this.querySelectorAll('.grid > article')
    this.activeFilters = new Set()
    this.noResultsMessage = this.querySelector('.no-results-message')

    // Reference the filter title and icon
    this.filterTitle = this.querySelector('.heading-lg')
    this.filterIcon = this.querySelector('.heading-lg svg')

    // Track if dropdown is open
    this.isDropdownOpen = false

    this.closeIcons = this.querySelectorAll('label svg')
    this.clickLinks = this.querySelectorAll('[data-click-link]')

    // Add reference to the All filter button
    this.allFilterButton = this.querySelector('.filter-all')

    // Initialize close icons
    this.closeIcons.forEach(icon => {
      icon.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
      icon.style.transform = 'scale(0.8)'
    })

    // Initialize All button styling
    if (this.allFilterButton) {
      this.allFilterButton.classList.add('active')
      // Style the All button when active
      this.updateAllButtonStyle()
    }
  }

  connectedCallback() {
    // Set up the event listeners for checkboxes
    this.filterInputs.forEach(input => {
      input.addEventListener('change', this.handleChange.bind(this))
    })

    // Add click handler for the filter title/heading to toggle dropdown
    if (this.filterTitle) {
      this.filterTitle.addEventListener('click', this.toggleFilterDropdown.bind(this))
    }

    this.clickLinks.forEach(link => {
      link.addEventListener('click', this.handleClickLink.bind(this))
    })

    // Add click handler for the All filter button
    if (this.allFilterButton) {
      this.allFilterButton.addEventListener('click', this.handleAllFilterClick.bind(this))
    }

    // Add window resize listener
    window.addEventListener('resize', this.handleResize.bind(this))

    // Initialize form state (hidden on mobile)
    this.updateFormVisibility()
  }

  disconnectedCallback() {
    // Clean up event listeners when component is removed
    window.removeEventListener('resize', this.handleResize.bind(this))
  }

  // Handle click on data-click-link elements
  handleClickLink(event) {
    const link = event.currentTarget
    if (link && link.dataset.clickLink) {
      window.location.href = link.dataset.clickLink
    }
  }

  // Handle click on All filter button
  handleAllFilterClick(event) {
    event.preventDefault()

    // Uncheck all checkboxes
    this.filterInputs.forEach(input => {
      if (input.checked) {
        input.checked = false

        // Reset label styling
        const label = this.querySelector(`label[for="${input.id}"]`)
        if (label) {
          // Get category classes from data attributes
          const categoryClass = label.dataset.categoryClass
          const categoryTextClass = label.dataset.categoryTextClass

          // Remove category-specific classes
          if (categoryClass) label.classList.remove(categoryClass)
          if (categoryTextClass) label.classList.remove(categoryTextClass)
          label.classList.add('border-secondary')

          // Hide the close icon
          const iconContainer = label.querySelector('span:last-child')
          if (iconContainer) {
            iconContainer.style.width = '0'
            iconContainer.style.marginLeft = '0'
          }
        }
      }
    })

    // Clear active filters and show all articles
    this.activeFilters.clear()
    this.showAllArticles()

    // Add active class to All button
    this.allFilterButton.classList.add('active')
    this.updateAllButtonStyle()

    // Hide no results message
    if (this.noResultsMessage) {
      this.noResultsMessage.style.display = 'none'
    }

    // Close dropdown on mobile
    if (window.innerWidth < 768) {
      this.isDropdownOpen = false
      this.updateFormVisibility()
      this.updateArrowIcon()
    }
  }

  // Update the styling of the All button based on its active state
  updateAllButtonStyle() {
    if (!this.allFilterButton) return

    if (this.allFilterButton.classList.contains('active')) {
      // Style for active state
      // this.allFilterButton.classList.add('bg-primary', 'text-white');
      this.allFilterButton.classList.remove('border-secondary')
    } else {
      // Style for inactive state
      // this.allFilterButton.classList.remove('bg-primary', 'text-white');
      this.allFilterButton.classList.add('border-secondary')
    }
  }

  toggleFilterDropdown(event) {
    // Toggle dropdown state
    this.isDropdownOpen = !this.isDropdownOpen

    // Update form visibility
    this.updateFormVisibility()

    // Rotate arrow icon
    this.updateArrowIcon()
  }

  updateFormVisibility() {
    if (!this.filterForm) return

    if (this.isDropdownOpen) {
      // Show the form by setting max-height
      this.filterForm.style.maxHeight = '100%'
      this.filterForm.style.paddingBottom = '0'
      this.filterForm.classList.add('border-opacity-100')
    } else {
      // Hide the form (only on mobile)
      if (window.innerWidth < 768) {
        // md breakpoint
        this.filterForm.style.maxHeight = '0'
        this.filterForm.style.paddingBottom = '0'
        this.filterForm.classList.remove('border-opacity-100')
      } else {
        // On desktop, form should always be visible
        this.filterForm.style.maxHeight = '100%'
        this.filterForm.style.paddingBottom = '0'
      }
    }
  }

  updateArrowIcon() {
    if (!this.filterIcon) return

    if (this.isDropdownOpen) {
      // Rotate arrow upward when open
      this.filterIcon.style.transform = 'rotate(180deg)'
    } else {
      // Reset arrow when closed
      this.filterIcon.style.transform = 'rotate(0deg)'
    }
  }

  // Handle window resize events
  handleResize() {
    // Always show form on desktop
    if (window.innerWidth >= 768 && !this.isDropdownOpen) {
      this.filterForm.style.maxHeight = '100%'
      this.filterForm.style.paddingBottom = '0'
    } else if (window.innerWidth < 768) {
      // Update form visibility based on dropdown state on mobile
      this.updateFormVisibility()
    }
  }

  handleChange(event) {
    const categoryId = event.target.id
    const isChecked = event.target.checked

    // Update active filters
    if (isChecked) {
      this.activeFilters.add(categoryId)
      // Add category-specific background class to label
      const label = this.querySelector(`label[for="${categoryId}"]`)
      if (label) {
        // Get category classes from data attributes
        const categoryClass = label.dataset.categoryClass
        const categoryTextClass = label.dataset.categoryTextClass

        // Add category-specific classes
        if (categoryClass) label.classList.add(categoryClass)
        if (categoryTextClass) label.classList.add(categoryTextClass)
        label.classList.remove('border-secondary')

        // Show the close icon with animation
        const iconContainer = label.querySelector('span:last-child')
        if (iconContainer) {
          iconContainer.style.width = '24px'
          iconContainer.style.marginLeft = '8px'
        }
      }

      // Remove active class from All button when filtering
      if (this.allFilterButton) {
        this.allFilterButton.classList.remove('active')
        this.updateAllButtonStyle()
      }
    } else {
      this.activeFilters.delete(categoryId)
      // Remove category-specific classes from label
      const label = this.querySelector(`label[for="${categoryId}"]`)
      if (label) {
        // Get category classes from data attributes
        const categoryClass = label.dataset.categoryClass
        const categoryTextClass = label.dataset.categoryTextClass

        // Remove category-specific classes
        if (categoryClass) label.classList.remove(categoryClass)
        if (categoryTextClass) label.classList.remove(categoryTextClass)
        label.classList.add('border-secondary')

        // Hide the close icon with animation
        const iconContainer = label.querySelector('span:last-child')
        if (iconContainer) {
          iconContainer.style.width = '0'
          iconContainer.style.marginLeft = '0'
        }
      }

      // If no filters are active, add active class to All button
      if (this.activeFilters.size === 0 && this.allFilterButton) {
        this.allFilterButton.classList.add('active')
        this.updateAllButtonStyle()
      }
    }

    // Apply filters
    this.applyFilters()

    // Close dropdown on mobile
    if (window.innerWidth < 768) {
      this.isDropdownOpen = false
      this.updateFormVisibility()
      this.updateArrowIcon()
    }
  }

  handleCloseClick(event) {
    event.preventDefault()
    event.stopPropagation()

    const label = event.target.closest('label')
    if (!label) return

    const input = label.querySelector('input')
    if (!input) return

    input.checked = false
    input.dispatchEvent(new Event('change'))
  }

  applyFilters() {
    // If no filters active, show all articles with animation
    if (this.activeFilters.size === 0) {
      this.showAllArticles()
      if (this.noResultsMessage) {
        this.noResultsMessage.style.display = 'none'
      }
      return
    }

    // First hide all articles
    this.articles.forEach(article => {
      article.style.opacity = '0'
      article.style.transform = 'translateY(20px)'
      article.style.display = 'none'
    })

    // Get articles that match the filters
    const matchingArticles = Array.from(this.articles).filter(article => {
      const categories = this.getArticleCategories(article)
      return [...this.activeFilters].some(filter => categories.includes(filter))
    })

    // Show/hide no results message
    if (this.noResultsMessage) {
      if (matchingArticles.length === 0) {
        this.noResultsMessage.style.display = 'block'
      } else {
        this.noResultsMessage.style.display = 'none'
      }
    }

    // Show filtered articles with staggered animation
    matchingArticles.forEach((article, index) => {
      // Set display first so the element is in the DOM
      article.style.display = ''

      // Add a small delay before starting the animation
      setTimeout(() => {
        article.style.transition = 'opacity 0.4s ease, transform 0.4s ease'
        article.style.opacity = '1'
        article.style.transform = 'translateY(0)'
      }, 50 * index) // 50ms delay between each article
    })
  }

  showAllArticles() {
    // First reset all articles to hidden state
    this.articles.forEach(article => {
      article.style.opacity = '0'
      article.style.transform = 'translateY(20px)'
      article.style.display = ''
    })

    // Hide no results message
    if (this.noResultsMessage) {
      this.noResultsMessage.style.display = 'none'
    }

    // Then animate them in one by one
    Array.from(this.articles).forEach((article, index) => {
      setTimeout(() => {
        article.style.transition = 'opacity 0.4s ease, transform 0.4s ease'
        article.style.opacity = '1'
        article.style.transform = 'translateY(0)'
      }, 50 * index) // 50ms delay between each article
    })
  }

  getArticleCategories(article) {
    // Extract categories from article
    const categoryElements = article.querySelectorAll('.paragraph-sm.uppercase')
    let categories = []

    // Extract category information
    categoryElements.forEach(element => {
      const categoryText = element.textContent.toLowerCase()
      const categoryLinks = element.querySelectorAll('a')

      categoryLinks.forEach(link => {
        const href = link.getAttribute('href')
        if (href) {
          const categorySlug = href.split('/').filter(Boolean).pop()
          if (categorySlug) {
            categories.push(categorySlug)
          }
        }
      })
    })

    // If no categories found in the text, try to determine from classes
    if (categories.length === 0) {
      const articleClasses = article.classList
      Array.from(articleClasses).forEach(className => {
        if (className.startsWith('category-')) {
          const categorySlug = className.replace('category-', '')
          categories.push(categorySlug)
        }
      })
    }

    return categories
  }

  get formData() {
    const formData = new FormData(this.filterForm)
    return Object.fromEntries(formData)
  }
}

if (!customElements.get('archive-filter')) {
  customElements.define('archive-filter', ArchiveFilter)
}
